import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { LoaderCircle } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Form } from '@components/form';
import { HandlersSelect } from '@components/HandlersSelect';
import { InputControlled } from '@components/input/controlled';
import { PageTitle } from '@components/PageTitle';
import { Anchor } from '@components/ui/anchor';
import { Button } from '@components/ui/button';
import { Separator } from '@components/ui/separator';
import { queryKeys } from '@constants/queryKeys.constant';
import { satisfactionOptions } from '@constants/satisfactions.constant';
import { ESmartEventTimelineKeyType } from '@enums/smartEvent.enum';
import { EStaleTime } from '@enums/staleTime.enum';
import { handleRequestError } from '@helpers/handleRequestError.helper';
import { invalidateSmartEventTimeline } from '@helpers/queryCache/invalidateSmartEventTimeline.helper';
import {
  updateServiceRequestSchema,
  type UpdateServiceRequestDto,
  type UpdateServiceRequestPayload,
} from '@services/serviceRequests/dtos/update.dto';
import { serviceRequestService } from '@services/serviceRequests/service';

function serviceRequestQueryOptions(id: string) {
  return {
    queryKey: queryKeys.serviceRequests.detail(id),
    queryFn: () => serviceRequestService.getById(id),
    staleTime: EStaleTime.OneHour,
  };
}

export const Route = createFileRoute(
  '/_layout/(private)/service-requests/$id/update',
)({
  loader: ({ context, params }) => {
    const options = serviceRequestQueryOptions(params.id);
    return context.queryClient.ensureQueryData({
      ...options,
      revalidateIfStale: true,
    });
  },
  pendingComponent: () => <LoaderCircle className="animate-spin" />,
  pendingMs: 200,
  pendingMinMs: 600,
  component: ServiceRequestUpdate,
});

function ServiceRequestUpdate() {
  const { queryClient } = Route.useRouteContext();
  const { id } = Route.useParams();
  const navigate = Route.useNavigate();

  const { data } = useSuspenseQuery(serviceRequestQueryOptions(id));

  const form = useForm<UpdateServiceRequestDto>({
    resolver: zodResolver(updateServiceRequestSchema),
    mode: 'onBlur',
    defaultValues: {
      satisfaction: data.serviceRequest.satisfaction,
      handlerId: data.serviceRequest.handlerId,
      description: data.serviceRequest.description,
      address: {
        street: data.serviceRequest.address.street,
        number: data.serviceRequest.address.number,
        postcode: data.serviceRequest.address.postcode,
        city: data.serviceRequest.address.city,
      },
      source: data.serviceRequest.source,
      automaticEmailsEnabled: data.serviceRequest.automaticEmailsEnabled,
    },
  });

  const { isPending, mutate: updateServiceRequest } = useMutation<
    void,
    unknown,
    UpdateServiceRequestPayload
  >({
    mutationFn: serviceRequestService.update,
    onError: handleRequestError,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.serviceRequests.detail(id),
      });
      queryClient.refetchQueries({
        queryKey: queryKeys.serviceRequests.detail(id),
      });
      invalidateSmartEventTimeline({
        id,
        queryClient,
        type: ESmartEventTimelineKeyType.ServiceRequest,
      });
      navigate({
        to: '..',
        replace: true,
      });
      toast.success('Success', {
        description: 'Service request updated successfully.',
      });
    },
  });

  const handleSubmit = form.handleSubmit(values => {
    updateServiceRequest({
      id,
      address: values.address,
      description: values.description,
      handlerId: values.handlerId,
      satisfaction: values.satisfaction,
      automaticEmailsEnabled: values.automaticEmailsEnabled,
    });
  });

  return (
    <Form.Provider {...form}>
      <PageTitle hasBackButton title="Update Service Request" />

      <form className="space-y-2" onSubmit={handleSubmit}>
        <InputControlled
          className="ml-auto"
          formItemProps={{ className: 'ml-auto w-fit' }}
          name="automaticEmailsEnabled"
          title="Enable automatic emails"
          variant="switch"
        />

        <InputControlled
          label="Description"
          maxLength={1000}
          name="description"
          rows={8}
          showCount
          variant="textarea"
        />
        <InputControlled disabled label="Source" name="source" />
        <HandlersSelect />

        <div className="grid gap-2.5 sm:grid-cols-2">
          <InputControlled label="Street" name="address.street" />
          <InputControlled label="Number" name="address.number" />
          <InputControlled label="Postcode" name="address.postcode" />
          <InputControlled label="City" name="address.city" />
        </div>

        <Separator variant="form" />

        <InputControlled
          label="Satisfaction"
          name="satisfaction"
          options={satisfactionOptions}
          variant="select"
        />

        <div className="mt-10 flex justify-center gap-x-2">
          <Anchor
            className="min-w-40 uppercase"
            disabled={isPending}
            rounded="lg"
            size="lg"
            to=".."
            variant="destructive"
          >
            Cancel
          </Anchor>
          <Button
            className="min-w-52"
            isLoading={isPending}
            size="lg"
            type="submit"
          >
            Save
          </Button>
        </div>
      </form>
    </Form.Provider>
  );
}
